import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Briefcase, Cpu, FileText, Heart, Laptop, ListChecks, Phone, Pill, Shirt, Sun, Bus,
  Cloud, CloudRain, Snowflake, Thermometer, MapPin, Mail, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  FileText, Laptop, Shirt, Sun, Heart, Pill, Bus, Briefcase, Phone, Cpu, ListChecks,
};

const PRIORITY_STYLE = {
  mandatory: { label: 'Must', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
  recommended: { label: 'Rec', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  do_not_carry: { label: 'Don\'t', cls: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200 line-through' },
};

export const PersonalizedTravelChecklist = ({ requestId, open, onOpenChange, canSendReminder = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !requestId) return;
    const fetchPersonalized = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/travel/requests/${requestId}/checklist`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load personalized checklist');
      } finally {
        setLoading(false);
      }
    };
    fetchPersonalized();
  }, [open, requestId]);

  const triggerReminder = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/travel/requests/${requestId}/send-reminder`, {}, { withCredentials: true });
      toast.success(res.data.sent ? `Reminder emailed to ${res.data.recipient}` : 'Failed to send reminder');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSending(false);
    }
  };

  const w = data?.weather?.summary;
  const isFallback = data?.weather?.fallback;
  const WeatherIcon = w?.any_snow ? Snowflake : w?.any_rain ? CloudRain : (w?.avg_high >= 28 ? Sun : Cloud);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" /> Personalized Travel Checklist
          </DialogTitle>
          <DialogDescription>
            Filtered for your destination, transport, and forecast weather.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-10 flex items-center justify-center text-sm text-muted-foreground gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Building your personalized list…
          </div>
        ) : (
          <div className="space-y-4">
            {/* Trip + Weather card */}
            <div className="rounded-xl border bg-gradient-to-br from-sky-50 to-indigo-50 dark:from-sky-950/30 dark:to-indigo-950/20 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="font-semibold">{data.destination || '—'}</span>
                    {data.weather?.country && <Badge variant="outline" className="text-[10px]">{data.weather.country}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {data.travel_type.toUpperCase()} · {data.category?.toUpperCase()} · {data.start_date || '—'}{data.end_date && data.end_date !== data.start_date ? ` → ${data.end_date}` : ''}
                  </p>
                </div>
                {w ? (
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/70 dark:bg-zinc-900/40 border" data-testid="weather-card">
                    <WeatherIcon className="w-6 h-6 text-primary" />
                    <div>
                      <p className="text-sm font-semibold">
                        <Thermometer className="w-3.5 h-3.5 inline -mt-0.5" /> {w.avg_low}°C – {w.avg_high}°C
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {w.primary_condition}{w.any_rain ? ` · Rain ${w.max_precip_prob}%` : ''}{w.any_snow ? ' · Snow' : ''}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Badge variant="outline" className="text-[11px]" data-testid="weather-fallback">
                    {isFallback ? `Forecast unavailable · using ${data.weather?.season || 'seasonal'} heuristic` : 'No destination weather'}
                  </Badge>
                )}
              </div>
              {data.climate && (
                <div className="flex flex-wrap gap-1.5 pt-1 border-t border-dashed">
                  {Object.entries(data.climate).filter(([k, v]) => v && !k.startsWith('_')).map(([k]) => (
                    <Badge key={k} variant="outline" className="text-[10px] capitalize">{k}</Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Sections */}
            {data.sections.map((sec) => {
              const Icon = ICON_MAP[sec.icon] || ListChecks;
              return (
                <div key={sec.section} className="rounded-lg border bg-card">
                  <div className="px-4 py-2.5 flex items-center gap-2 border-b">
                    <Icon className="w-4 h-4 text-primary" />
                    <p className="font-semibold text-sm">{sec.section}</p>
                    <span className="text-xs text-muted-foreground">· {sec.subcategories.reduce((n, s) => n + s.items.length, 0)} items</span>
                  </div>
                  {sec.subcategories.map((sub) => (
                    <div key={sub.name} className="px-4 py-2 border-b last:border-b-0">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{sub.name}</p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                        {sub.items.map((it) => {
                          const style = PRIORITY_STYLE[it.priority] || PRIORITY_STYLE.recommended;
                          return (
                            <li key={it.id} className="flex items-center gap-2 text-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
                              <span className="flex-1 truncate">{it.name}</span>
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-0 ${style.cls}`}>{style.label}</Badge>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })}

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
              {canSendReminder && (
                <Button variant="outline" size="sm" className="gap-1.5" onClick={triggerReminder} disabled={sending} data-testid="send-reminder-now">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  Email this checklist now
                </Button>
              )}
              <Button size="sm" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PersonalizedTravelChecklist;
